import { Box } from '@lifeomic/chroma-react/components/Box/Box';
import { Skeleton } from '@lifeomic/chroma-react/components/Skeleton/Skeleton';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { CodingSystem } from '@lifeomic/fhir-constants';
import { Theme } from '@mui/material/styles';
import clsx from 'clsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAsync, useUpdateEffect } from 'react-use';
import { useService } from '../../../../../../../hooks';
import { useActiveProjectCohortId } from '../../../../../../../hooks/useActiveProjectCohortId';
import { useActiveProjectId } from '../../../../../../../hooks/useActiveProjectId';
import MedicalResultsService, {
  MedicalResultSource,
  ResultHistory,
} from '../../../../../../../services/MedicalResultsService';
import { MedicalResultHistory } from '../../../../LabResults/hooks/useFetchMedicalResultHistories';
import { formatResultHistoryCodingForDisplay } from '../../../../LabResults/formatters';
import { MeasurementCode } from '../../../types';
import { groupMedicalResultsByUnit } from '../../../util/fhir';
import { InlineRangeView } from '../RangeViewModalV2';
import { CategoryGraph } from './CategoryGraph';
import { Graph } from './Graph';
import { GRAPH_HEIGHT } from './constants';
import { intoObservationValue, shouldUseCategoryGraph } from './utils';

const medicalResultsService = new MedicalResultsService();

const usePrintStyles = makeStyles(() => ({
  avoidBreakInside: {
    '@media print': {
      '&': {
        pageBreakInside: 'avoid',
      },
    },
  },
}));

const useStyles = makeStyles<{
  contained?: boolean;
}>((theme: Theme) => ({
  graphWrapper: {
    width: '100%',
    height: `${GRAPH_HEIGHT + 10}px`,
    cursor: 'default',
    background: '#ffffff',
    border: ({ contained }) => (contained ? 'none' : `solid 1px #262c3226`),
    borderRadius: 4,
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
    position: 'relative',
    maxWidth: 800,
  },
  unitPicker: {
    position: 'absolute',
    top: theme.spacing(1.5),
    right: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
}));

export type MedicalResultChartCondensedProps = {
  dimension: MeasurementCode;
  patient: fhir.Patient;
  contained?: boolean;
  source?: MedicalResultSource;
};

export function MedicalResultChartCondensed({
  dimension,
  patient,
  contained,
  source,
}: MedicalResultChartCondensedProps) {
  const cohort = useActiveProjectCohortId();
  const activeProject = useActiveProjectId();

  const [fetchMedicalResultsHistory] = useService(
    medicalResultsService.getHistory,
  );

  const patientId = patient.id as string;
  const dimensionCode = dimension.codes[0].key as string;
  const dimensionSystem = dimension.codes[0].system as string;

  const [hasStartedLoading, setHasStartedLoading] = useState(false);

  const { value, loading } = useAsync(async () => {
    setHasStartedLoading(true);
    const response = await fetchMedicalResultsHistory({
      patientId,
      project: activeProject,
      code: dimensionCode,
      system: dimensionSystem,
      cohort,
      includeClinicOnly: true,
      source,
    });
    return {
      ...response.data,
      items: formatResultHistoryCodingForDisplay(
        response.data.items,
        source === 'memberUpload',
      ),
    };
  }, [
    patientId,
    dimensionCode,
    dimensionSystem,
    cohort,
    activeProject,
    source,
  ]);

  const resultsHistory = useMemo(
    () => ({ medicalResultHistory: value?.items ?? [] }),
    [value?.items],
  );

  if (loading || !hasStartedLoading) {
    return <Skeleton height={`${GRAPH_HEIGHT + 10}px`} width="100%" />;
  }

  return (
    <MedicalResultChartCondensedWithPrefetchedData
      resultsHistory={resultsHistory}
      contained={contained}
    />
  );
}

function MedicalResultChartCondensedWithPrefetchedData({
  resultsHistory,
  contained,
}: {
  resultsHistory: Pick<MedicalResultHistory, 'medicalResultHistory'>;
  contained?: boolean;
}) {
  const printClasses = usePrintStyles();

  const categoryGraphEnabled = useMemo(
    () => shouldUseCategoryGraph(resultsHistory.medicalResultHistory),
    [resultsHistory.medicalResultHistory],
  );

  const groupedResults = useMemo(() => {
    return groupMedicalResultsByUnit(resultsHistory.medicalResultHistory);
  }, [resultsHistory.medicalResultHistory]);

  const units = useMemo(() => {
    return Object.keys(groupedResults);
  }, [groupedResults]);

  const [selectedUnit, setSelectedUnit] = useState<string>(units[0]);

  useUpdateEffect(() => {
    if (units.length > 0) setSelectedUnit(units[0]);
  }, [units]);

  const medicalResults: ResultHistory[] | undefined = useMemo(() => {
    if (categoryGraphEnabled) {
      return resultsHistory.medicalResultHistory;
    }
    return groupedResults[selectedUnit];
  }, [
    groupedResults,
    selectedUnit,
    categoryGraphEnabled,
    resultsHistory.medicalResultHistory,
  ]);

  const graphableResults: ResultHistory[] | undefined = useMemo(() => {
    return medicalResults?.filter(r => {
      const val = intoObservationValue(r);
      return val && !isNaN(val.value);
    });
  }, [medicalResults]);

  if (!graphableResults || graphableResults.length <= 1) {
    return (
      <Box
        data-testid={getDataTestId(medicalResults ?? [])}
        style={{ flex: 1 }}
        className={printClasses.avoidBreakInside}
        paddingBottom={contained ? 1 : 0}
        paddingLeft={contained ? 2.5 : 0}
        paddingRight={contained ? 8 : 0}
      >
        <Box fullWidth direction="column">
          {units.length > 1 && (
            <UnitPicker
              units={units}
              selectedUnit={selectedUnit}
              onSelect={setSelectedUnit}
            />
          )}
          <InlineRangeView.WithPrefetchedData
            medicalResults={resultsHistory.medicalResultHistory}
            selectedUnit={selectedUnit}
            onUnitChange={setSelectedUnit}
            withGraph={false}
            showPending={true}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      fullWidth
      onClick={e => e.stopPropagation()}
      className={printClasses.avoidBreakInside}
    >
      <Box
        fullWidth
        direction="column"
        paddingLeft={contained ? 2.5 : 0}
        paddingRight={contained ? 8 : 0}
      >
        {units.length > 1 && (
          <UnitPicker
            units={units}
            selectedUnit={selectedUnit}
            onSelect={setSelectedUnit}
          />
        )}
        <GraphWrapper
          medicalResults={graphableResults}
          contained={contained}
          categoryGraphEnabled={categoryGraphEnabled}
        />
      </Box>
    </Box>
  );
}

MedicalResultChartCondensed.WithPrefetchedData =
  MedicalResultChartCondensedWithPrefetchedData;

type GraphWrapperProps = {
  medicalResults: ResultHistory[];
  categoryGraphEnabled: boolean;
  contained?: boolean;
};

export function GraphWrapper({
  medicalResults,
  categoryGraphEnabled,
  contained,
}: GraphWrapperProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const containerWidth = useElementWidth(containerRef);
  const printClasses = usePrintStyles();
  const classes = useStyles({ contained });

  return (
    <div
      data-testid={getDataTestId(medicalResults)}
      ref={containerRef}
      className={clsx(printClasses.avoidBreakInside, classes.graphWrapper)}
    >
      {categoryGraphEnabled ? (
        <CategoryGraph
          medicalResults={medicalResults}
          containerWidth={containerWidth}
        />
      ) : (
        <Graph
          medicalResults={medicalResults}
          containerWidth={containerWidth}
        />
      )}
    </div>
  );
}

const useUnitPickerStyles = makeStyles(theme => ({
  unitPicker: {
    border: `1px solid ${theme.palette.graphite[100]}`,
    borderRadius: 6,
    padding: theme.pxToRem(2),
    backgroundColor: theme.palette.graphite[50],
    display: 'inline-flex',
    gap: theme.pxToRem(2),
    width: 'fit-content',
  },
  unitPickerButton: {
    border: 'none',
    backgroundColor: 'transparent',
    padding: `${theme.pxToRem(1)} ${theme.pxToRem(12)}`,
    borderRadius: 4,
    fontSize: theme.typography.caption.fontSize,
    fontWeight: 500,
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: theme.palette.graphite[100],
      color: theme.palette.text.primary,
    },
  },
  selectedUnitButton: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(0, 0, 0, 0.05)',
    '&:hover': {
      backgroundColor: theme.palette.common.white,
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.06)',
    },
  },
}));

type UnitPickerProps = {
  units: string[];
  selectedUnit: string;
  onSelect: (unit: string) => void;
};

function UnitPicker({ units, selectedUnit, onSelect }: UnitPickerProps) {
  const classes = useUnitPickerStyles();

  return (
    <Box direction="row" gap={1} align="center" className={classes.unitPicker}>
      <Box
        direction="row"
        gap={1}
        align="center"
        paddingLeft={1}
        paddingRight={1}
      >
        <Text weight="bold" size="caption">
          Unit:
        </Text>
      </Box>
      {units.map(unit => {
        const isSelected = unit === selectedUnit;
        return (
          <button
            key={unit}
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              onSelect(unit);
            }}
            className={clsx(classes.unitPickerButton, {
              [classes.selectedUnitButton]: isSelected,
            })}
          >
            <Text size="caption" weight={isSelected ? 'bold' : 'regular'}>
              {unit || 'Unknown'}
            </Text>
          </button>
        );
      })}
    </Box>
  );
}

function useElementWidth(ref: React.RefObject<HTMLElement>) {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const currentRef = ref.current;

    if (currentRef) {
      setWidth(currentRef.clientWidth);
    }

    const resizeObserver = new ResizeObserver(entries => {
      if (entries[0] && currentRef) {
        setWidth(currentRef.clientWidth);
      }
    });

    if (currentRef) {
      resizeObserver.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        resizeObserver.unobserve(currentRef);
      }
      resizeObserver.disconnect();
    };
    // need to depend on ref.current in order to trigger on component mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, ref.current]);

  return width;
}

function getDataTestId(result: ResultHistory[]): string {
  return (
    result[0]?.resource?.code?.coding?.find(
      code => code.system === CodingSystem.LOINC,
    )?.display ?? 'unknown'
  );
}
